/**
 * Manage Team page (/team, admin only).
 *
 * Backed by the /api/auth/admin/* endpoints:
 *   GET  /admin/staff           - list accounts + enrolment state
 *   POST /admin/create-staff    - provision account, returns temp password
 *   POST /admin/reset-password  - new temp password (shown once, in-app)
 *   POST /admin/reset-totp      - wipe TOTP so the user re-pairs a phone
 *   POST /admin/remove-staff    - delete an account (guard-railed)
 *
 * Temp passwords are displayed in-app ONCE (never emailed) - the admin
 * passes them on personally. This mirrors the original seeding flow.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Check,
  Copy,
  KeyRound,
  Loader2,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Trash2,
  UserPlus,
} from "lucide-react";
import StaffShell from "../components/staff/StaffShell";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

function TempPasswordBlock({ email, password }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy - select the text manually");
    }
  };
  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-2"
      data-testid="temp-password-block"
    >
      <p className="text-sm text-amber-900">
        Temporary password for <span className="font-medium">{email}</span> —
        shown once, share it with them directly:
      </p>
      <div className="flex items-center gap-2">
        <code
          className="flex-1 font-mono text-base bg-white border border-amber-200 rounded px-3 py-2 select-all"
          data-testid="temp-password-value"
        >
          {password}
        </code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={copy}
          data-testid="temp-password-copy-btn"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      <p className="text-xs text-amber-700">
        They'll be asked to set their own password and pair an authenticator
        app on first login.
      </p>
    </div>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [staff, setStaff] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("staff");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // {email, temp_password}
  const [resetResult, setResetResult] = useState(null); // {email, temp_password}
  const [confirmAction, setConfirmAction] = useState(null); // {type, email}
  const [rowBusy, setRowBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/auth/admin/staff`);
      setStaff(r.data.staff || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Couldn't load the team list"
      );
      setStaff([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createStaff = async () => {
    const email = newEmail.trim().toLowerCase();
    const name = newName.trim();
    if (!email || !name) {
      toast.error("Name and email are both required");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/auth/admin/create-staff`, {
        email,
        name,
        role: newRole,
      });
      setCreated(r.data);
      setNewName("");
      setNewEmail("");
      setNewRole("staff");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the account");
    } finally {
      setBusy(false);
    }
  };

  const runAction = async () => {
    if (!confirmAction) return;
    const { type, email } = confirmAction;
    setConfirmAction(null);
    setRowBusy(email);
    try {
      if (type === "reset-password") {
        const r = await axios.post(`${API}/api/auth/admin/reset-password`, {
          email,
        });
        setResetResult(r.data);
      } else if (type === "reset-totp") {
        await axios.post(`${API}/api/auth/admin/reset-totp`, { email });
        toast.success(`TOTP reset for ${email} - they'll re-pair on next login`);
      } else if (type === "remove") {
        await axios.post(`${API}/api/auth/admin/remove-staff`, { email });
        toast.success(`${email} removed`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setRowBusy(null);
    }
  };

  const confirmCopy = {
    "reset-password": {
      title: "Reset password?",
      body: "A new temporary password will be generated and shown to you once. Their current password stops working immediately.",
      cta: "Reset password",
    },
    "reset-totp": {
      title: "Reset authenticator?",
      body: "Their authenticator pairing is wiped - they'll scan a fresh QR code on next login. Their password is unchanged.",
      cta: "Reset TOTP",
    },
    remove: {
      title: "Remove this account?",
      body: "They lose access immediately. This can't be undone (you can re-create the account later).",
      cta: "Remove account",
    },
  }[confirmAction?.type] || {};

  return (
    <StaffShell>
      <div className="max-w-4xl space-y-8" data-testid="team-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Manage team
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              Provision staff accounts, reset passwords and authenticator
              pairings. Temporary passwords are shown once, in-app only.
            </p>
          </div>
          <Button
            onClick={() => {
              setCreated(null);
              setAddOpen(true);
            }}
            data-testid="team-add-btn"
            className="bg-[#1B4F8A] hover:bg-[#173f6e]"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add team member
          </Button>
        </div>

        {resetResult && (
          <TempPasswordBlock
            email={resetResult.email}
            password={resetResult.temp_password}
          />
        )}

        <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="team-table">
            <thead>
              <tr className="bg-stone-100/60 text-left">
                <th className="px-4 py-2.5 font-medium text-stone-700">Name</th>
                <th className="px-4 py-2.5 font-medium text-stone-700">Role</th>
                <th className="px-4 py-2.5 font-medium text-stone-700">
                  Security
                </th>
                <th className="px-4 py-2.5 font-medium text-stone-700">
                  Last login
                </th>
                <th className="px-4 py-2.5 font-medium text-stone-700 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {staff === null && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-stone-400">
                    <Loader2 className="w-5 h-5 animate-spin inline" />
                  </td>
                </tr>
              )}
              {staff?.map((s) => {
                const isSelf =
                  s.email.toLowerCase() === (user?.email || "").toLowerCase();
                return (
                  <tr
                    key={s.email}
                    className="border-t border-stone-100"
                    data-testid={`team-row-${s.email}`}
                  >
                    <td className="px-4 py-3">
                      <p className="text-slate-900 font-medium">
                        {s.name || "—"}
                        {isSelf && (
                          <span className="ml-2 text-[10px] font-mono uppercase tracking-wide text-stone-400">
                            you
                          </span>
                        )}
                      </p>
                      <p className="text-stone-500 text-xs">{s.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          "inline-block text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-0.5 rounded " +
                          (s.role === "admin"
                            ? "bg-[#1B4F8A]/10 text-[#1B4F8A]"
                            : "bg-stone-100 text-stone-600")
                        }
                      >
                        {s.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1 text-xs">
                        <span className="inline-flex items-center gap-1.5">
                          {s.must_change_password ? (
                            <>
                              <KeyRound className="w-3.5 h-3.5 text-amber-500" />
                              <span className="text-amber-700">
                                Temp password
                              </span>
                            </>
                          ) : (
                            <>
                              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                              <span className="text-stone-600">
                                Password set
                              </span>
                            </>
                          )}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          {s.totp_confirmed_at ? (
                            <>
                              <Smartphone className="w-3.5 h-3.5 text-emerald-600" />
                              <span className="text-stone-600">
                                Authenticator paired
                              </span>
                            </>
                          ) : (
                            <>
                              <ShieldOff className="w-3.5 h-3.5 text-stone-400" />
                              <span className="text-stone-400">
                                No authenticator
                              </span>
                            </>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-stone-600 whitespace-nowrap">
                      {fmtDate(s.last_login_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {rowBusy === s.email ? (
                          <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                        ) : (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-xs"
                              onClick={() =>
                                setConfirmAction({
                                  type: "reset-password",
                                  email: s.email,
                                })
                              }
                              data-testid={`team-reset-password-${s.email}`}
                            >
                              Reset password
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-xs"
                              onClick={() =>
                                setConfirmAction({
                                  type: "reset-totp",
                                  email: s.email,
                                })
                              }
                              data-testid={`team-reset-totp-${s.email}`}
                            >
                              Reset TOTP
                            </Button>
                            {!isSelf && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() =>
                                  setConfirmAction({
                                    type: "remove",
                                    email: s.email,
                                  })
                                }
                                data-testid={`team-remove-${s.email}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add member dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent data-testid="team-add-dialog">
            <DialogHeader>
              <DialogTitle>Add team member</DialogTitle>
              <DialogDescription>
                A temporary password is generated and shown to you once —
                pass it on directly. They set their own password and pair an
                authenticator on first login.
              </DialogDescription>
            </DialogHeader>
            {created ? (
              <TempPasswordBlock
                email={created.email}
                password={created.temp_password}
              />
            ) : (
              <div className="space-y-3">
                <Input
                  placeholder="Full name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  data-testid="team-add-name-input"
                />
                <Input
                  placeholder="email@medicysltd.co.uk"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  data-testid="team-add-email-input"
                />
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  data-testid="team-add-role-select"
                  className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 focus:outline-none focus:border-[#1B4F8A]"
                >
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            )}
            <DialogFooter>
              {created ? (
                <Button
                  onClick={() => setAddOpen(false)}
                  data-testid="team-add-done-btn"
                >
                  Done
                </Button>
              ) : (
                <Button
                  onClick={createStaff}
                  disabled={busy}
                  data-testid="team-add-submit-btn"
                  className="bg-[#1B4F8A] hover:bg-[#173f6e]"
                >
                  {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create account
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm dialog for row actions */}
        <AlertDialog
          open={confirmAction != null}
          onOpenChange={(open) => {
            if (!open) setConfirmAction(null);
          }}
        >
          <AlertDialogContent data-testid="team-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmCopy.title}</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="font-medium text-stone-700">
                  {confirmAction?.email}
                </span>{" "}
                — {confirmCopy.body}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="team-confirm-cancel">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={runAction}
                data-testid="team-confirm-go"
                className={
                  confirmAction?.type === "remove"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-[#1B4F8A] hover:bg-[#173f6e]"
                }
              >
                {confirmCopy.cta}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </StaffShell>
  );
}
